import { forwardRef, ReactNode, HTMLAttributes } from 'react';
import styles from './Card.module.scss';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
    /** The card content */
    readonly children: ReactNode;
    /** Color variant based on design system skin colors */
    readonly color?: 'blue' | 'gray' | 'green' | 'olive' | 'orange' | 'red' | 'violet' | 'yellow';
    /** Shadow elevation level */
    readonly elevation?: 'none' | 'raised' | 'overlay';
    /** Padding size */
    readonly padding?: 'sm' | 'md' | 'lg';
    /** Remove padding entirely */
    readonly noPadding?: boolean;
    /** Additional CSS classes */
    readonly className?: string;
}

/**
 * Card component for displaying content in elevated containers.
 * Follows the Bane NOR design system patterns.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
    {
        children,
        color,
        elevation = 'raised',
        padding = 'md',
        noPadding = false,
        className,
        ...rest
    },
    ref
) {
    const colorClass = color ? styles[`bn-card--${color}`] : undefined;
    const elevationClass = elevation !== 'none' ? styles[`bn-card--elevation-${elevation}`] : undefined;
    const paddingClass = !noPadding ? styles[`bn-card--padding-${padding}`] : undefined;

    const componentClassNames = [
        styles['bn-card'],
        colorClass,
        elevationClass,
        paddingClass,
        className
    ].filter(Boolean).join(' ');

    return (
        <div ref={ref} className={componentClassNames} {...rest}>
            {children}
        </div>
    );
});

Card.displayName = 'Card';

export default Card;
